<template>
    <section
        id="pitch-showcase"
        class="w-full bg-[rgb(13,13,13)] py-16 lg:py-24 px-4 lg:px-24 relative z-10"
    >
        <div class="max-w-[1280px] mx-auto">

            <!-- Now Casting -->
            <div v-if="nowCasting.length > 0" class="mb-20">
                <div class="flex flex-col lg:flex-row">
                    <div class="mb-8 lg:mb-10">
                        <div class="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-400 font-semibold mb-3 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                            <Video class="w-3.5 h-3.5" />
                            Casting Open
                        </div>
                        <h2 class="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-3">Now Casting</h2>
                        <p class="text-white/60 text-base lg:text-lg max-w-[600px]">
                            These pitches are fully funded and ready to film — they just need creators to submit auditions.
                        </p>
                    </div>
                    <div class="hidden md:flex ml-auto self-start md:self-end pb-4">
                        <button
                            @click="exploreAllPitches"
                            class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                        >
                            Explore more pitches
                            <ArrowRight class="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div class="flex overflow-x-auto snap-x snap-mandatory gap-4 lg:gap-6 pb-8 -mx-4 px-4 lg:-mx-6 lg:px-6 hide-scrollbar">
                    <div
                        v-for="pitch in nowCasting"
                        :key="pitch.id"
                        class="min-w-[300px] w-[85vw] sm:w-[340px] lg:w-[380px] shrink-0 snap-center"
                    >
                        <PitchCard :pitch="pitch" variant="casting" @click="handleGoToPitchNowCasting(pitch.slug)" />
                    </div>
                </div>
                <div class="md:ml-auto self-start md:self-end pb-2 md:hidden">
                    <button
                        @click="exploreAllPitches"
                        class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                    >
                        Explore more pitches
                        <ArrowRight class="w-4 h-4" />
                    </button>
                </div>
            </div>

            <!-- In the Race -->
            <div v-if="inRace.length > 0" class="mb-20">
                <div class="flex flex-col lg:flex-row">
                    <div class="mb-8 lg:mb-10">
                        <div class="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-violet-400 font-semibold mb-3 bg-violet-500/10 px-3 py-1 rounded-full border border-violet-500/20">
                            <Trophy class="w-3.5 h-3.5" />
                            Live Now
                        </div>
                        <h2 class="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-3">Auditions In The Race</h2>
                        <p class="text-white/60 text-base lg:text-lg max-w-[600px]">
                            Backed by the community — creators are now competing for the winning audition.
                        </p>
                    </div>
                    <div class="hidden md:flex ml-auto self-start md:self-end pb-4">
                            <button
                                @click="exploreAllAuditions"
                                class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                            >
                                Explore more auditions
                                <ArrowRight class="w-4 h-4" />
                            </button>
                        </div>
                </div>
                <div class="flex overflow-x-auto snap-x snap-mandatory gap-4 lg:gap-6 pb-8 -mx-4 px-4 lg:-mx-6 lg:px-6 hide-scrollbar">
                    <div
                        v-for="pitch in inRace"
                        :key="pitch.id"
                        class="min-w-[300px] w-[85vw] sm:w-[340px] lg:w-[380px] shrink-0 snap-center"
                    >
                        <PitchCard :pitch="pitch" variant="featured" @click="handleGoToPitchInRace(pitch.slug)" />
                    </div>
                </div>
                <div class="md:ml-auto self-start md:self-end pb-2 md:hidden">
                    <button
                        @click="exploreAllPitches"
                        class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                    >
                        Explore more pitches
                        <ArrowRight class="w-4 h-4" />
                    </button>
                </div>
            </div>

            <!-- Trending / Near Goal -->
            <div v-if="trending.length > 0" class="mb-20">
                <div class="flex flex-col lg:flex-row">
                    <div class="mb-8 flex items-end justify-between gap-4">
                        <div>
                            <div class="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-400 font-semibold mb-3">
                                <Flame class="w-3.5 h-3.5" />
                                Trending
                            </div>
                            <h2 class="text-2xl lg:text-3xl font-bold text-white tracking-tight">Pitches Trending</h2>
                        </div>
                    </div>
                    <div class="hidden md:flex ml-auto self-start md:self-end pb-4">
                        <button
                            @click="exploreAllPitches"
                            class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                        >
                            Explore more pitches
                            <ArrowRight class="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div class="flex overflow-x-auto snap-x snap-mandatory gap-4 lg:gap-6 pb-8 -mx-4 px-4 lg:mx-0 lg:px-0 hide-scrollbar">
                    <div v-for="pitch in trending" :key="pitch.id" class="snap-center shrink-0">
                        <PitchCard :pitch="pitch" variant="medium" @click="handleGoToPitchTrending(pitch.slug)" />
                    </div>
                </div>
                <div class="md:ml-auto self-start md:self-end pb-2 md:hidden">
                    <button
                        @click="exploreAllPitches"
                        class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                    >
                        Explore more pitches
                        <ArrowRight class="w-4 h-4" />
                    </button>
                </div>
            </div>

            <!-- Fresh / Just Dropped -->
            <div v-if="fresh.length > 0" class="mb-12">
                <div class="flex flex-col lg:flex-row">
                    <div class="mb-8">
                        <div class="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-3">
                            <Sparkles class="w-3.5 h-3.5" />
                            New from Producers
                        </div>
                        <h2 class="text-2xl lg:text-3xl font-bold text-white tracking-tight">Just Dropped</h2>
                    </div>
                    <div class="hidden md:flex ml-auto self-start md:self-end pb-4">
                            <button
                                @click="exploreAllPitches"
                                class="inline-flex items-center gap-2  text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 hover:scale-[1.02]"
                            >
                                Explore more pitches
                                <ArrowRight class="w-4 h-4" />
                            </button>
                        </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    <PitchCard
                        v-for="pitch in fresh"
                        :key="pitch.id"
                        :pitch="pitch"
                        variant="compact"
                        @click="handleGoToPitchJustDropped(pitch.slug)"
                    />
                </div>
            </div>

            <!-- Empty state: no pitches at all -->
            <div v-if="!hasAnyPitches" class="text-center py-24">
                <p class="text-white/30 text-base">No pitches yet — be the first to create one.</p>
            </div>

            <!-- CTA -->
            <div v-if="hasAnyPitches" class="flex justify-center mt-16">
                <button
                    @click="exploreAllPitches"
                    class="inline-flex items-center gap-2 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.1] text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02]"
                >
                    Explore All Productions
                    <ArrowRight class="w-4 h-4" />
                </button>
            </div>

        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { ArrowRight, Flame, Sparkles, Trophy, Video } from 'lucide-vue-next'
import PitchCard from './PitchCard.vue'
import {trackEvent, trackFathomEvent} from "@/Helpers/analytics";

const props = defineProps({
    nowCasting: { type: Array, default: () => [] },
    inRace:     { type: Array, default: () => [] },
    trending:   { type: Array, default: () => [] },
    fresh:      { type: Array, default: () => [] },
})

const emit = defineEmits(['explore'])

const hasAnyPitches = computed(() =>
    props.nowCasting.length + props.inRace.length + props.trending.length + props.fresh.length > 0
)

function goToPitch(slug) {
    router.visit('/studio/castings/' + slug)
}

const exploreAllPitches = () => {
    emit('explore');
}
const exploreAllAuditions = () => {
    emit('exploreAuditions');
}
const handleGoToPitchNowCasting = (slug) => {
    trackFathomEvent('Producer_Audition_NowCasting_Click')
    trackEvent('Producer NowCasting Audition Clicked')

    goToPitch(slug)
}
const handleGoToPitchInRace = (slug) => {
    trackFathomEvent('Producer_Audition_InRace_Click')
    trackEvent('Producer InRace Audition Clicked')

    goToPitch(slug)
}
const handleGoToPitchTrending = (slug) => {
    trackFathomEvent('Producer_Audition_Trending_Click')
    trackEvent('Producer Trending Audition Clicked')

    goToPitch(slug)
}
const handleGoToPitchJustDropped = (slug) => {
    trackFathomEvent('Producer_Audition_JustDroped_Click')
    trackEvent('Producer JustDropped Casting Clicked')

    goToPitch(slug)
}

</script>

<style scoped>
.hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
.hide-scrollbar::-webkit-scrollbar { display: none; }
</style>
